<template>
  <div class="container-fluid p-0">
    <div class="row m-0 justify-content-center">
      <div class="col-12 col-md-10 col-lg-8 p-0">

        <div v-if="expired" class="text-center py-5">
          <div class="card shadow-sm mx-3 my-4">
            <div class="card-body py-5">
              <div class="mb-4">
                <i class="fa fa-clock fa-3x text-muted"></i>
              </div>
              <h4 class="mb-3">This link has expired</h4>
              <p class="text-muted mb-4">
                This ride status link is no longer active. Please ask the person who shared it with you to send a new link.
              </p>
              <div class="mt-4 pt-3 border-top">
                <p class="text-muted small mb-2">Don't have Asher yet?</p>
                <a href="#" class="btn btn-primary btn-lg px-4">Download Asher</a>
                <p class="text-muted small mt-3">Get safe, reliable rides at your fingertips.</p>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3 text-muted">Loading ride status...</p>
        </div>

        <div v-else-if="rideData" class="pb-4">

          <div class="bg-primary text-white px-3 py-3 d-flex align-items-center justify-content-between">
            <div>
              <h5 class="mb-0 fw-bold">Asher Rides</h5>
              <small>Live Ride Status</small>
            </div>
            <div>
              <span class="badge rounded-pill" :class="statusBadgeClass">{{ statusLabel }}</span>
            </div>
          </div>

          <div v-if="rideData.rideActive" class="position-relative">
            <div id="shared-ride-map" class="w-100" style="height: 350px;"></div>
            <button v-if="viewerTookControl"
                    type="button"
                    class="btn btn-light shadow-sm rounded-circle position-absolute"
                    style="right: 12px; bottom: 12px; width: 40px; height: 40px;"
                    title="Show the whole route"
                    @click="recentre">
              <i class="fa fa-expand text-primary"></i>
            </button>
          </div>

          <div v-if="rideData.rideCompleted" class="text-center py-5 mx-3">
            <div class="card shadow-sm">
              <div class="card-body py-5">
                <div class="mb-3">
                  <i class="fa fa-check-circle fa-4x text-success"></i>
                </div>
                <h4 class="mb-2">Ride Completed Safely</h4>
                <p class="text-muted">
                  The rider has arrived at their destination.
                </p>
                <p class="text-muted small" v-if="rideData.orderCompletionDateTime">
                  Completed at {{ formatDateTime(rideData.orderCompletionDateTime) }}
                </p>
              </div>
            </div>
          </div>

          <div v-if="!rideData.rideCompleted && !rideData.rideActive" class="text-center py-5 mx-3">
            <div class="card shadow-sm">
              <div class="card-body py-4">
                <div class="mb-3">
                  <i class="fa fa-info-circle fa-3x text-muted"></i>
                </div>
                <h5 class="mb-2">Ride {{ rideData.rideStatus === 'CANCELLED' ? 'Cancelled' : 'Ended' }}</h5>
                <p class="text-muted">This ride is no longer active.</p>
              </div>
            </div>
          </div>

          <div class="mx-3 mt-3">
            <div class="card shadow-sm mb-3" v-if="rideData.rideActive || rideData.rideCompleted">
              <div class="card-body">
                <h6 class="fw-bold mb-3"><i class="fa fa-route me-2"></i>Trip Details</h6>

                <div class="d-flex align-items-start mb-2">
                  <div class="me-3 text-center" style="min-width: 20px;">
                    <i class="fa fa-circle text-success" style="font-size: 10px;"></i>
                  </div>
                  <div>
                    <small class="text-muted">Pickup</small>
                    <div class="fw-medium">{{ rideData.pickupLocation?.address || 'Location not available' }}</div>
                  </div>
                </div>

                <div v-if="rideData.stops && rideData.stops.length > 0">
                  <div v-for="stop in rideData.stops" :key="stop.stopOrder" class="d-flex align-items-start mb-2">
                    <div class="me-3 text-center" style="min-width: 20px;">
                      <i class="fa fa-circle text-warning" style="font-size: 10px;"></i>
                    </div>
                    <div>
                      <small class="text-muted">Stop {{ stop.stopOrder }}</small>
                      <div class="fw-medium">{{ stop.address || 'Stop location' }}</div>
                    </div>
                  </div>
                </div>

                <div class="d-flex align-items-start">
                  <div class="me-3 text-center" style="min-width: 20px;">
                    <i class="fa fa-map-marker-alt text-danger" style="font-size: 14px;"></i>
                  </div>
                  <div>
                    <small class="text-muted">Drop-off</small>
                    <div class="fw-medium">{{ rideData.dropOffLocation?.address || 'Location not available' }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card shadow-sm mb-3" v-if="rideData.driverFirstName && rideData.rideActive">
              <div class="card-body">
                <h6 class="fw-bold mb-2"><i class="fa fa-id-badge me-2"></i>Driver</h6>
                <div class="d-flex align-items-center">
                  <div class="bg-light rounded-circle d-flex align-items-center justify-content-center me-3"
                       style="width: 48px; height: 48px;">
                    <i class="fa fa-user text-muted fa-lg"></i>
                  </div>
                  <div>
                    <div class="fw-bold">{{ rideData.driverFirstName }}</div>
                    <div class="text-muted small" v-if="rideData.vehicleDescription">
                      {{ rideData.vehicleColor ? rideData.vehicleColor + ' ' : '' }}{{ rideData.vehicleDescription }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card shadow-sm mb-3" v-if="rideData.rideActive">
              <div class="card-body text-center">
                <div class="mb-2">
                  <i class="fa fa-shield-alt text-primary fa-2x"></i>
                </div>
                <h6 class="fw-bold">Ride Monitoring Active</h6>
                <p class="text-muted small mb-0">
                  This ride is being tracked for safety. Status updates are live.
                </p>
              </div>
            </div>
          </div>

          <div class="mx-3 mt-4 mb-3">
            <div class="card border-primary shadow-sm">
              <div class="card-body text-center py-4">
                <h5 class="fw-bold mb-2">Get Asher Rides</h5>
                <p class="text-muted mb-3">Safe, reliable rides at your fingertips. Download the app and get your first ride today.</p>
                <a href="#" class="btn btn-primary btn-lg px-4 mb-2">Download Now</a>
                <p class="text-muted small mt-2 mb-0">Available on iOS and Android</p>
              </div>
            </div>
          </div>

          <div class="text-center py-3">
            <small class="text-muted">&copy; {{ new Date().getFullYear() }} Asher Rides. All rights reserved.</small>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';

const props = defineProps({
  token: { type: String, required: true },
  googleMapsApiKey: { type: String, default: '' },
  initialExpired: { type: Boolean, default: false }
});

const rideData = ref(null);
const loading = ref(!props.initialExpired);
const expired = ref(props.initialExpired);
const error = ref(null);

// True once the viewer has panned or zoomed for themselves. From that point the
// camera stops re-framing on every poll — otherwise a viewer who zoomed in on
// the driver was thrown back out to the whole route fifteen seconds later.
const viewerTookControl = ref(false);

// Not reactive: none of this is rendered, and Vue's proxy around a Google Maps
// object breaks identity comparisons inside the SDK.
let map = null;
let pickupMarker = null;
let dropOffMarker = null;
let driverMarker = null;
let stopMarkers = [];
let routeLine = null;
let pollingInterval = null;
let hasFramedRoute = false;
let lastDriverPosition = null;
let driverBearing = 0;

const POLL_MS = 15000;

/// The rider app's own palette, so the share link and the app the rider is
/// holding show the same ride in the same colours.
const COLOR_PICKUP = '#28C76F';
const COLOR_STOP = '#ff8000';
const COLOR_DESTINATION = '#e83330';
const COLOR_ROUTE = '#007AFF';

/**
 * The distance the driver must have covered before the arrow is re-aimed.
 *
 * The API carries no heading, so the bearing is derived from consecutive polled
 * positions. At a 15-second poll this is roughly 5 km/h — below it the movement
 * is as likely to be GPS scatter as travel, and aiming at scatter makes the
 * arrow spin on the spot while the car is stationary.
 */
const MIN_BEARING_METRES = 20;

const statusLabel = computed(() => {
  if (!rideData.value) return '';
  const labels = {
    PENDING: 'Finding Driver',
    ACCEPTED: 'Driver Assigned',
    TO_PICKUP: 'Driver En Route',
    WAITING_PICKUP: 'Driver Arrived',
    IN_PROGRESS: 'In Progress',
    STOPPED: 'At a Stop',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
    DRIVER_NOT_FOUND: 'No Driver Found'
  };
  return labels[rideData.value.rideStatus] || rideData.value.rideStatus;
});

const statusBadgeClass = computed(() => {
  if (!rideData.value) return 'bg-secondary';
  const classes = {
    PENDING: 'bg-warning text-dark',
    ACCEPTED: 'bg-info',
    TO_PICKUP: 'bg-info',
    WAITING_PICKUP: 'bg-info',
    IN_PROGRESS: 'bg-success',
    STOPPED: 'bg-warning text-dark',
    COMPLETED: 'bg-success',
    CANCELLED: 'bg-danger',
    DRIVER_NOT_FOUND: 'bg-secondary'
  };
  return classes[rideData.value.rideStatus] || 'bg-secondary';
});

function svgIcon(markup) {
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(markup);
}

/**
 * The circle-and-stem pin the rider app uses, in the requested colour.
 *
 * Drawn rather than loaded from the app's PNGs so it can be recoloured and
 * numbered without shipping four more binaries; the geometry is traced from
 * assets/icon/map_marker.png so the two read as the same marker.
 */
function pinIcon(color, { hollow = false } = {}) {
  const inner = hollow
    ? `<circle cx="396" cy="282" r="100" fill="#ffffff"/>`
    : `<path d="M396 130 a105 105 0 0 0 -105 105 c0 58 78 155 100 183 a6 6 0 0 0 10 0 c22 -28 100 -125 100 -183 a105 105 0 0 0 -105 -105 z" fill="#ffffff"/>
       <circle cx="396" cy="235" r="48" fill="${color}"/>`;
  return svgIcon(
    `<svg xmlns="http://www.w3.org/2000/svg" width="792" height="792" viewBox="0 0 792 792">
       <circle cx="396" cy="282" r="247" fill="${color}"/>
       ${inner}
       <path d="M370 548 L396 790 L422 548 Z" fill="${color}"/>
     </svg>`
  );
}

/**
 * The rider app's driver arrow (assets/markers/driver_marker.svg), rotated to
 * the given bearing.
 *
 * Google's `Icon` has no rotation of its own, so the turn is baked into the
 * artwork. The art points north about its own centre, which is why it is
 * anchored centrally rather than at a tip.
 */
function driverIcon(bearing) {
  return svgIcon(
    `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96">
       <g transform="rotate(${bearing.toFixed(1)} 48 48)">
         <path d="M48 8 L74 78 L48 64 L22 78 Z" fill="#191b1f" stroke="#fbfaf7" stroke-width="6" stroke-linejoin="round"/>
         <path d="M48 8 L60 40 L48 34 L36 40 Z" fill="#7a55e8"/>
       </g>
     </svg>`
  );
}

function pinMarkerOptions(color, opts = {}) {
  return {
    url: pinIcon(color, opts),
    scaledSize: new google.maps.Size(46, 46),
    anchor: new google.maps.Point(23, 46),
    labelOrigin: new google.maps.Point(23, 16)
  };
}

function latLng(location) {
  if (!location) return null;
  const { latitude, longitude } = location;
  if (typeof latitude !== 'number' || typeof longitude !== 'number') return null;
  return { lat: latitude, lng: longitude };
}

async function fetchRideStatus() {
  try {
    loading.value = !rideData.value;
    const response = await fetch(`/api/ride/shared-status?token=${props.token}`);
    if (!response.ok) {
      error.value = 'Unable to load ride status.';
      loading.value = false;
      return;
    }

    const data = await response.json();

    if (data.expired) {
      expired.value = true;
      loading.value = false;
      stopPolling();
      return;
    }

    rideData.value = data;
    loading.value = false;

    if (data.rideActive) {
      await nextTick();
      initOrUpdateMap();
      startPolling();
    } else {
      stopPolling();
    }
  } catch (err) {
    console.error('Error fetching ride status:', err);
    error.value = 'Unable to load ride status. Please try again later.';
    loading.value = false;
  }
}

function startPolling() {
  if (pollingInterval) return;
  pollingInterval = setInterval(fetchRideStatus, POLL_MS);
}

function stopPolling() {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
}

function createMap(mapEl) {
  map = new google.maps.Map(mapEl, {
    zoom: 14,
    disableDefaultUI: true,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false
  });

  // A raw pointer on the map element, not a camera event: `zoom_changed` and
  // `center_changed` also fire for our own fitBounds, so using those would have
  // the map hand control away to a viewer who never touched it.
  ['mousedown', 'touchstart', 'wheel'].forEach((evt) => {
    mapEl.addEventListener(evt, () => { viewerTookControl.value = true; }, { passive: true });
  });
}

function drawRoute(bounds) {
  const encoded = rideData.value.routePolyline;
  const encoding = window.google && google.maps.geometry && google.maps.geometry.encoding;
  if (!encoded || !encoding) {
    // No route resolved server-side. Leave whatever line is already drawn: a
    // single failed poll should not blank the route for the rest of the ride.
    return;
  }

  const path = encoding.decodePath(encoded);
  if (!path.length) return;

  if (!routeLine) {
    routeLine = new google.maps.Polyline({
      map,
      path,
      strokeColor: COLOR_ROUTE,
      strokeOpacity: 0.85,
      strokeWeight: 6
    });
  } else {
    routeLine.setPath(path);
  }

  path.forEach((point) => bounds.extend(point));
}

function drawStops(bounds) {
  const stops = (rideData.value.stops || []).filter((stop) => latLng(stop));

  // Rebuilt rather than reconciled: the itinerary is editable mid-ride, so a
  // stop can be removed and the markers must not outlive it.
  stopMarkers.forEach((marker) => marker.setMap(null));
  stopMarkers = [];

  stops.forEach((stop) => {
    const pos = latLng(stop);
    stopMarkers.push(new google.maps.Marker({
      position: pos,
      map,
      title: stop.address || `Stop ${stop.stopOrder}`,
      icon: pinMarkerOptions(COLOR_STOP, { hollow: true }),
      label: {
        text: String(stop.stopOrder),
        color: COLOR_STOP,
        fontSize: '11px',
        fontWeight: '700'
      }
    }));
    bounds.extend(pos);
  });
}

function drawDriver(bounds) {
  const pos = latLng(rideData.value.driverLocation);
  if (!pos) return;

  const spherical = window.google && google.maps.geometry && google.maps.geometry.spherical;
  if (lastDriverPosition && spherical) {
    const from = new google.maps.LatLng(lastDriverPosition);
    const to = new google.maps.LatLng(pos);
    if (spherical.computeDistanceBetween(from, to) >= MIN_BEARING_METRES) {
      driverBearing = spherical.computeHeading(from, to);
    }
  }
  lastDriverPosition = pos;

  const icon = {
    url: driverIcon(driverBearing),
    scaledSize: new google.maps.Size(42, 42),
    anchor: new google.maps.Point(21, 21)
  };

  if (!driverMarker) {
    driverMarker = new google.maps.Marker({ position: pos, map, title: 'Driver', icon, zIndex: 10 });
  } else {
    driverMarker.setPosition(pos);
    driverMarker.setIcon(icon);
  }
  bounds.extend(pos);
}

function initOrUpdateMap() {
  const mapEl = document.getElementById('shared-ride-map');
  if (!mapEl || !window.google || !rideData.value) return;

  if (!map) createMap(mapEl);

  const bounds = new google.maps.LatLngBounds();

  const pickup = latLng(rideData.value.pickupLocation);
  if (pickup) {
    if (!pickupMarker) {
      pickupMarker = new google.maps.Marker({
        position: pickup,
        map,
        title: 'Pickup',
        icon: pinMarkerOptions(COLOR_PICKUP, { hollow: true })
      });
    } else {
      pickupMarker.setPosition(pickup);
    }
    bounds.extend(pickup);
  }

  const dropOff = latLng(rideData.value.dropOffLocation);
  if (dropOff) {
    if (!dropOffMarker) {
      dropOffMarker = new google.maps.Marker({
        position: dropOff,
        map,
        title: 'Drop-off',
        icon: pinMarkerOptions(COLOR_DESTINATION)
      });
    } else {
      dropOffMarker.setPosition(dropOff);
    }
    bounds.extend(dropOff);
  }

  drawStops(bounds);
  drawRoute(bounds);
  drawDriver(bounds);

  if (bounds.isEmpty()) return;
  if (hasFramedRoute && viewerTookControl.value) return;

  hasFramedRoute = true;
  map.fitBounds(bounds, { top: 40, bottom: 40, left: 40, right: 40 });
}

/** Re-frames the whole ride after the viewer has moved the map themselves. */
function recentre() {
  viewerTookControl.value = false;
  initOrUpdateMap();
}

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (e) {
    return dateStr;
  }
}

onMounted(() => {
  if (!expired.value) fetchRideStatus();
});

onBeforeUnmount(stopPolling);
</script>

<style scoped>
.bg-primary {
  background-color: #4f46e5 !important;
}

.btn-primary {
  background-color: #4f46e5;
  border-color: #4f46e5;
}

.btn-primary:hover {
  background-color: #4338ca;
  border-color: #4338ca;
}

.border-primary {
  border-color: #4f46e5 !important;
}
</style>
